import { useUserEventLogger } from '@repo/tracking-service';
import { useDialog } from '@repo/ui';
import { Button, useToast } from '@zenterprise-inc/ui';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from 'react-relay';

import { initCheckedCanSurveyQuery } from '@/__generated__/initCheckedCanSurveyQuery.graphql';
import { checkedCanSurveyQuery } from '@/graphql/query/survey/init';
import { COMMON_PATHS, SURVEY_PAGES } from '@/lib/constants/paths';
import { useRefundHistoryId } from '@/lib/hooks/use-refund-history-id';
import { BusinessType } from '@/lib/types/request';

export const CompleteCta = ({ businessType }: { businessType: BusinessType }) => {
  const toast = useToast();
  const { sendClickEvent } = useUserEventLogger();
  const router = useRouter();

  // indis일 때만 쿼리 로딩
  const [queryRef, loadQuery] = useQueryLoader<initCheckedCanSurveyQuery>(checkedCanSurveyQuery);
  const { getRefundHistoryId } = useRefundHistoryId();
  const [loading, setLoading] = useState(false);
  const [hasTried, setHasTried] = useState(false);

  useEffect(() => {
    if (businessType !== 'indis') return;
    setLoading(true);
    getRefundHistoryId().then((refundHistoryId) => {
      setHasTried(true);
      if (refundHistoryId) {
        loadQuery({ refundHistoryId });
      }
      setLoading(false);
    });
  }, [businessType, getRefundHistoryId, loadQuery]);

  const handleClick = () => {
    sendClickEvent('indis-request_apply-complete_finish');
    router.push(COMMON_PATHS.환급조회목록);
  };

  if (businessType !== 'indis') return null;

  if (loading) {
    return (
      <Button.StickyBottom.Button state="primary" disabled>
        불러오는 중...
      </Button.StickyBottom.Button>
    );
  }

  if (queryRef) {
    return <PreloadedQueryCTA queryRef={queryRef} />;
  }

  if (router.query.onlyTritx === 'true') {
    return (
      <Button.StickyBottom.Button state="primary" onClick={handleClick}>
        확인
      </Button.StickyBottom.Button>
    );
  }
  return (
    <CTA
      onClick={() => {
        if (hasTried && !queryRef) {
          toast({ content: '알 수 없는 오류입니다.', icon: 'error', iconColor: 'red.600' });
        }
      }}
    />
  );
};

function PreloadedQueryCTA({ queryRef }: { queryRef: PreloadedQuery<initCheckedCanSurveyQuery> }) {
  const router = useRouter();
  const { sendClickEvent } = useUserEventLogger();
  const { getSurveyStatus } = usePreloadedQuery<initCheckedCanSurveyQuery>(checkedCanSurveyQuery, queryRef);
  const { showDialog } = useDialog();
  const toast = useToast();

  const handleClick = () => {
    if (router.query.onlyTritx === 'true') {
      router.push(COMMON_PATHS.환급조회목록);
      return;
    }

    sendClickEvent('indis-request_apply-complete_next');
    if (getSurveyStatus.result) {
      router.push({
        pathname: SURVEY_PAGES.설문시작,
        query: router.query
      });
      return;
    } else if (getSurveyStatus.errors?.code) {
      showDialog({
        title: '더 빠른 환급을 위해 필요한 설문을\n알림톡으로 보내드릴게요.',
        description: '*20시 이후에 신청하신 고객은 다음날 오전 10시에 보내드릴게요.',
        buttons: {
          primary: {
            text: '확인',
            onClick: () => router.push(COMMON_PATHS.환급조회목록)
          }
        }
      });
      return;
    }
    toast({ content: '알 수 없는 오류입니다.', icon: 'error', iconColor: 'red.600' });
  };

  return <CTA onClick={handleClick} title={router.query.onlyTritx === 'true' ? '확인' : ''} />;
}

function CTA({ onClick, title }: { onClick: () => void; title?: string }) {
  return (
    <Button.StickyBottom.Button state="primary" onClick={onClick}>
      {title || `더 빠른 환급 받아보기`}
    </Button.StickyBottom.Button>
  );
}
